import { MobileDeviceFilters } from '@reliance/types/filters/MobileDeviceFilters';
import { createColumnHelper } from '@tanstack/react-table';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import { MobileDeviceWithAppVersions, useMobileDevices } from '../hooks/MobileDevice';
import InmateDetailBlock from '../inmate/InmateDetailBlock';
import BaseHtmlTable from '../table/BaseHtmlTable';
import { useServerTableFilterState } from '../table/useServerTableFilterState';

export type MobileDeviceTableColumn =
  | 'dateAdded'
  | 'lastContact'
  | 'status'
  | 'facilityIds'
  | 'inmate'
  | 'name'
  | 'serialNumber'
  | 'batteryLevel'
  | 'productName'
  | 'osVersion'
  | 'applications'
  | 'messagingAppVersion'
  | 'kioskAppVersion'
  | 'adminAppVersion'
  | 'infoAppVersion'
  | 'options';

type TableProps = {
  facilityId?: number;
  hiddenColumns?: Array<MobileDeviceTableColumn>;
  enableRowSelection?: boolean;
  onRowSelectionChange?: (rows: MobileDeviceWithAppVersions[]) => void;
  onUpdateApps?: (device: MobileDeviceWithAppVersions) => void;
  onPoll?: (device: MobileDeviceWithAppVersions) => void;
  initialFilters?: Partial<MobileDeviceFilters>;
  refetchTrigger?: number;
  getRowClassName?: (row: MobileDeviceWithAppVersions) => string;
  getCellClassName?: (row: MobileDeviceWithAppVersions, columnId: string) => string;
  onLoadingChange?: (loading: boolean) => void;
  updatingDeviceIds?: number[];
  pollingDeviceIds?: number[];
};

const Table: React.FC<TableProps> = ({
  facilityId,
  hiddenColumns,
  enableRowSelection,
  onRowSelectionChange,
  onUpdateApps,
  onPoll,
  initialFilters,
  refetchTrigger,
  getRowClassName,
  getCellClassName,
  onLoadingChange,
  updatingDeviceIds = [],
  pollingDeviceIds = []
}) => {
  const queryColumnDefs = useMemo(() => {
    const list: { id: string; meta?: Record<string, unknown> }[] = [
      { id: 'dateAdded', meta: { filterComponent: 'DateRangeFilter', sortName: 'dateAdded' } },
      { id: 'lastContact', meta: { filterComponent: 'DateRangeFilter', sortName: 'lastContact' } },
      { id: 'status', meta: { filterComponent: 'TextFilter', sortName: 'status' } },
    ];
    if (!facilityId) {
      list.push({
        id: 'facilityIds',
        meta: { filterComponent: 'FacilityIdsFilter', sortName: 'facility.name' },
      });
    }
    list.push(
      { id: 'inmate', meta: { sortName: ['inmate.lastName', 'inmate.firstName'] } },
      { id: 'name', meta: { filterComponent: 'TextFilter', sortName: 'name' } },
      { id: 'serialNumber', meta: { filterComponent: 'TextFilter', sortName: 'serialNumber' } },
      { id: 'batteryLevel', meta: { sortName: 'batteryLevel' } },
      { id: 'productName', meta: { sortName: 'productName' } },
      { id: 'osVersion', meta: { sortName: 'osVersion' } },
      { id: 'applications', meta: { sortName: 'application' } },
      { id: 'messagingAppVersion' },
      { id: 'kioskAppVersion' },
      { id: 'adminAppVersion' },
      { id: 'infoAppVersion' },
      { id: 'options' }
    );
    return list;
  }, [facilityId]);

  const baseRecord = useMemo(
    () =>
      ({
        facilityId: facilityId != null ? String(facilityId) : undefined,
        orderBy: '-dateAdded',
        ...initialFilters,
      }) as Record<string, unknown>,
    [facilityId, initialFilters]
  );
  const initialSorting = useMemo(() => {
    const orderBy = initialFilters?.orderBy;
    if (typeof orderBy === 'string' && orderBy.includes('facility.name')) {
      return [
        { id: 'facilityIds', desc: false },
        { id: 'name', desc: false },
      ];
    }
    return [{ id: 'dateAdded', desc: true }];
  }, [initialFilters?.orderBy]);
  const tableState = useServerTableFilterState(queryColumnDefs, baseRecord, {
    initialPagination: { pageIndex: 0, pageSize: 100 },
    initialSorting,
  });
  const filtersState = useMemo(
    (): MobileDeviceFilters => tableState.mergedRecord as MobileDeviceFilters,
    [tableState.mergedRecord]
  );
  const { result, loading, error, execute } = useMobileDevices(filtersState);

  const handleRowSelectionChange = useCallback(
    (rows: MobileDeviceWithAppVersions[]) => {
      onRowSelectionChange?.(rows);
    },
    [onRowSelectionChange]
  );

  useEffect(() => {
    if (refetchTrigger != null && refetchTrigger > 0) {
      execute();
    }
  }, [refetchTrigger, execute]);

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  const columnHelper = createColumnHelper<MobileDeviceWithAppVersions>();
  const columns = [
    columnHelper.accessor('dateAdded', {
      header: 'Date Added',
      filterFn: 'dateRange',
      meta: {
        filterComponent: 'DateRangeFilter',
        startDate: moment().startOf('day').subtract(20, 'year')
      },
      cell: data => moment(String(data.getValue().date)).format('MM/DD/YYYY HH:mm:ss'),
    }),
    columnHelper.accessor('lastContact', {
      header: 'Last Contact',
      filterFn: 'dateRange',
      meta: {
        filterComponent: 'DateRangeFilter',
        startDate: moment().startOf('day').subtract(20, 'year')
      },
      cell: data => data.getValue() ? moment(String(data.getValue().date)).format('MM/DD/YYYY HH:mm:ss') : '',
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      meta: {
        filterComponent: 'TextFilter',
        sortName: 'status'
      }
    }),
    ...(!facilityId ? [
      columnHelper.accessor('facility.name', {
        id: 'facilityIds',
        header: 'Facility',
        filterFn: 'facilityIds',
        cell: info => info.row.original.facility ? (
          <>
            {info.getValue()}
            <br/>
            <small><em>{info.row.original.facility.city}, {info.row.original.facility.state}</em></small>
          </>
        ) : 'Unassigned',
        meta: {
          filterComponent: 'FacilityIdsFilter',
          sortName: 'facility.name'
        }
      })
    ] : []),
    columnHelper.accessor((row) => {
        if (row.inmateDevice) {
          return `${row.inmateDevice.inmate.firstName} ${row.inmateDevice.inmate.middleName} ${row.inmateDevice.inmate.lastName}`;
        }
        return '';
      }, {
      id: 'inmate',
      header: 'Inmate',
      enableColumnFilter: false, // API isn't set up for this well yet without using an inmate picker
      cell: info => info.row.original.inmateDevice?.inmate ? (
        <InmateDetailBlock inmate={info.row.original.inmateDevice.inmate} />
      ) : null,
      meta: { sortName: ['inmate.lastName', 'inmate.firstName'] }
    }),
    columnHelper.accessor('name', {
      header: 'Name',
      meta: {
        filterComponent: 'TextFilter',
        sortName: 'name'
      }
    }),
    columnHelper.accessor('serialNumber', {
      header: 'Serial Number',
      meta: {
        filterComponent: 'TextFilter',
        sortName: 'serialNumber'
      }
    }),
    columnHelper.accessor('batteryLevel', {
      header: 'Battery',
      enableColumnFilter: false,
      cell: info => (
        <span>
          {(info.getValue() * 100).toFixed(0)}%
          {info.row.original.charging && <i className="fas fa-bolt" title="Charging" />}
        </span>
      ),
      meta: { sortName: 'batteryLevel' }
    }),
    columnHelper.accessor('productName', {
      header: 'Product Name',
      meta: {
        sortName: 'productName'
      }
    }),
    columnHelper.accessor('osVersion', {
      header: 'iOS Version',
      meta: {
        sortName: 'osVersion'
      }
    }),
    columnHelper.accessor('application', {
      id: 'applications', // used for filtering
      header: 'Application',
      meta: {
        sortName: 'application'
      }
    }),
    columnHelper.display({
      id: 'messagingAppVersion',
      header: 'Connect Messaging Version',
      cell: info => (
        info.row.original.assignedMessagingAppVersion ? (
          <span>
            {info.row.original.installedMessagingAppVersion || 'Not Installed'}
            {info.row.original.installedMessagingAppVersion !== info.row.original.assignedMessagingAppVersion && (
              <span> ({info.row.original.assignedMessagingAppVersion} Assigned)</span>
            )}
          </span>
        ) : <span>Not Assigned</span>
      )
    }),
    columnHelper.display({
      id: 'kioskAppVersion',
      header: 'Connect Kiosk Version',
      cell: info => (
        info.row.original.assignedKioskAppVersion ? (
          <span>
            {info.row.original.installedKioskAppVersion || 'Not Installed'}
            {info.row.original.installedKioskAppVersion !== info.row.original.assignedKioskAppVersion && (
              <span> ({info.row.original.assignedKioskAppVersion} Assigned)</span>
            )}
          </span>
        ) : <span>Not Assigned</span>
      )
    }),
    columnHelper.display({
      id: 'adminAppVersion',
      header: 'Connect Admin Version',
      cell: info => (
        info.row.original.assignedAdminAppVersion ? (
          <span>
            {info.row.original.installedAdminAppVersion || 'Not Installed'}
            {info.row.original.installedAdminAppVersion !== info.row.original.assignedAdminAppVersion && (
              <span> ({info.row.original.assignedAdminAppVersion} Assigned)</span>
            )}
          </span>
        ) : <span>Not Assigned</span>
      )
    }),
    columnHelper.display({
      id: 'infoAppVersion',
      header: 'Connect Info Version',
      cell: info => (
        info.row.original.assignedInfoAppVersion ? (
          <span>
            {info.row.original.installedInfoAppVersion || 'Not Installed'}
            {info.row.original.installedInfoAppVersion !== info.row.original.assignedInfoAppVersion && (
              <span> ({info.row.original.assignedInfoAppVersion} Assigned)</span>
            )}
          </span>
        ) : <span>Not Assigned</span>
      )
    }),
    columnHelper.display({
      id: 'options',
      header: '',
      cell: (info) => {
        const device = info.row.original;
        return (
          <div className="btn-group" role="group">
            {onUpdateApps && (
              <Button
                variant="info"
                size="sm"
                disabled={loading || updatingDeviceIds.includes(device.id)}
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdateApps(device);
                }}>
                Update
              </Button>
            )}
            {onPoll && (
              <Button
                variant="secondary"
                size="sm"
                disabled={loading || pollingDeviceIds.includes(device.id)}
                onClick={(e) => {
                  e.stopPropagation();
                  onPoll(device);
                }}>
                Poll
              </Button>
            )}
            <Link
              to={`/mobile-device/view/${device.id}`}
              className="btn btn-secondary btn-sm text-decoration-none">
              View
            </Link>
          </div>
        );
      }
    })
  ];

  if (error) {
    return <div className="alert alert-danger">Failed to load mobile devices: {error.message}</div>;
  }

  return (
    <BaseHtmlTable
      rows={result?.data ?? []}
      columnDefs={columns}
      loading={loading}
      page={result?.page}
      totalPages={result?.totalPages}
      total={result?.total}
      perPage={result?.perPage ?? tableState.pagination.pageSize}
      paginationMode="server"
      serverPagination={tableState.serverPagination}
      onServerPaginationChange={tableState.onServerPaginationChange}
      serverSorting={tableState.serverSorting}
      onServerSortingChange={tableState.onServerSortingChange}
      serverColumnFilters={tableState.serverColumnFilters}
      onServerColumnFiltersChange={tableState.onServerColumnFiltersChange}
      enableRowSelection={enableRowSelection}
      onRowSelectionChange={handleRowSelectionChange}
      hiddenColumns={hiddenColumns}
      getRowClassName={getRowClassName}
      getCellClassName={getCellClassName}
    />
  );
};

export default Table;
